/* eslint-disable */
import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Filter, ExternalLink, Eye, Info } from 'lucide-react';
import GlobalSearch from '@/Components/globalSearch/search';
import VariantTable from '@/Pages/Food Products/VariantPrice/VariantTable';
import { Button } from '@/Components/ui/button';
import drawerActions from '@/Actions/drawer.actions';
import modalActions from '@/Actions/modal.actions';
import TransitPoDetailsModal from './TransitPoDetailsModal';
import { urls } from '@/Constants';
import wastageAction from '@/Actions/wastage.action';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import moment from 'moment';
import ImagesFinder from '@/Components/ImagesFinder/ImagesFinder';
import { filtersInfoChip, getHigherUnitAndQuantity, handleExcelDownload } from '@/Helpers';
import { Tooltip } from '@mui/material';

const columns = [
    {
        title: 'Truck Number',
        key: 'truckNumber',
        fixed: true,
        width: 120,
    },
    {
        title: 'PO Details',
        key: 'poDetails',
        fixed: true,
        width: 120,
    },
    {
        title: 'Transit Date',
        key: 'transitDate',
        sortable: true,
    },
    {
        title: 'Transit Time',
        key: 'transitTime',
    },
    {
        title: 'Dispatched Price',
        key: 'dispatchedQty',
        sortable: true,
    },
    {
        title: 'Received Price',
        key: 'receivedQty',
        sortable: true,
    },
    {
        title: 'Loss Price',
        key: 'lossQty',
        sortable: true,
    },
    {
        title: 'Loss %',
        key: 'lossPercent',
        sortable: true,
    },
    {
        title: 'Status',
        key: 'status',
    },
    {
        title: 'Recovered Price',
        key: 'recoveredAtKitchen',
    },
    {
        title: 'Action',
        key: 'action',
    },
];

const filtersInfo = {
    header: {
        date: false,
        store: false,
    },
    kitchenBaseStore: {
        text: 'Select Base Kitchen Store',
        show: true,
    },
    dateOnly: {
        text: 'Date Range',
        show: true,
    },
};


const StatusBadge = ({ status, statusType }) => {
    const getStatusStyles = () => {
        switch (statusType) {
            case 'AUTO':
                return 'bg-blue-100 text-blue-600 border-blue-200';
            case 'MANUAL':
                return 'bg-green-100 text-green-600 border-green-200';
            default:
                return 'bg-orange-100 text-orange-600 border-orange-200';
        }
    };

    return (
        <div className={`px-2 py-1 text-xs text-center font-medium rounded-full text-nowrap ${getStatusStyles()}`}>
            {status === 'AUTO' ? 'AUTO ACCEPTED' : status === 'MANUAL' ? 'MANUAL ADJUSTMENT' : 'PENDING ADJUSTMENT'}
        </div>
    );
};

function TransitLoss() {
    const [search, setSearch] = useState(null);
    const dispatch = useDispatch();
    const history = useHistory();
    const { loading, transitLossList, faliureRequest } = useSelector((state) => state.wastage);
    const handleOpenPoDetails = (rowData) => {
        dispatch(modalActions.toggleTransitPoDetailsModal(true, rowData));
    };
    const [filters, setFilters] = useState({});
    const params = new URLSearchParams(history.location.search);
    const flag = params.get('flag');

    const fetchList = async (filters) => {
        setFilters(filters);
        const req = {
            data: {
                fromStore: filters?.fromPodFilter?.id,
                date: moment(filters?.dateOnly?.date).subtract(1, 'day').format('YYYY-MM-DD'),
            },
        };
        dispatch(wastageAction.fetchTransitLossList(req));
    }

    const filteredData = useCallback(() => {
        if (!search?.searchValue || search?.searchValue?.trim() === '') {
            return transitLossList;
        }

        const searchTerm = search.searchValue.toLowerCase();

        return transitLossList?.filter((item) => {
            const fieldValue = item?.vehicleNo?.toString().toLowerCase() || '';
            return fieldValue.includes(searchTerm);
        });
    }, [transitLossList, search]);

    const customRowData = {
        truckNumber: (obj) => {
            return (
                <span
                    className='text-nowrap text-[12px]'
                >
                    {obj?.vehicleNo}
                </span>
            );
        },
        poDetails: (obj) => {
            return (
                <span
                    className="text-blue-600 text-nowrap cursor-pointer hover:underline"
                    onClick={() => handleOpenPoDetails(obj)}
                >
                    {obj?.poOrders?.length} Orders
                </span>
            );
        },
        transitDate: (obj) => {
            const date = new Date(obj?.poOrders?.[0]?.PoStatusDateData?.[0]?.createdAt);
            const formattedDate = moment(date).format('DD-MM-YYYY');
            return (
                <span
                    className='text-nowrap text-[12px]'
                >
                    {formattedDate}
                </span>
            );
        },
        transitTime: (obj) => {
            const startime = moment(obj?.poOrders?.[0]?.PoStatusDateData?.[0]?.createdAt).format('hh:mm A');
            const endTime = moment(obj?.poOrders?.[0]?.PoStatusDateData?.[0]?.endedAt).format('hh:mm A');
            return (
                <span
                    className='text-nowrap text-[12px]'
                >
                    {startime} - {endTime}
                </span>
            );
        },
        dispatchedQty: (obj) => {
            const dispatchedPrice = obj?.poOrders?.reduce?.((acc, po) => {
                return acc + (po?.items?.reduce((sum, item) => {
                    const [convertedQty] = getHigherUnitAndQuantity(item?.unit, item?.fulfilled || 0);
                    const basePrice = item?.productCost || 0;
                    const effectivePrice = item?.taxPercentage ? basePrice * (1 + item?.taxPercentage / 100) : basePrice;
                    const price = Number(convertedQty) * effectivePrice;
                    return sum + price;
                }, 0));
            }, 0);
            return (
                <span className='text-nowrap text-[12px]'>
                    ₹{Number(dispatchedPrice || 0).toFixed(2)}
                </span>
            );
        },
        receivedQty: (obj) => {
            const receivedPrice = obj?.poOrders?.reduce?.((acc, po) => {
                return acc + (po?.items?.reduce((sum, item) => {
                    const [convertedQty] = getHigherUnitAndQuantity(item?.unit, item?.storeFulfilled || 0);
                    const basePrice = item?.productCost || 0;
                    const effectivePrice = item?.taxPercentage ? basePrice * (1 + item.taxPercentage / 100) : basePrice;
                    const price = Number(convertedQty) * effectivePrice;
                    return sum + price;
                }, 0));
            }, 0);
            return (
                <span className='text-nowrap text-[12px]'>
                    ₹{Number(receivedPrice || 0).toFixed(2)}
                </span>
            );
        },
        lossQty: (obj) => {
            const lossPrice = obj?.poOrders?.reduce?.((acc, po) => {
                return acc + (po?.items?.reduce((sum, item) => {
                    const [convertedFulfilled] = getHigherUnitAndQuantity(item?.unit, item?.fulfilled || 0);
                    const [convertedStoreFulfilled] = getHigherUnitAndQuantity(item?.unit, item?.storeFulfilled || 0);
                    const basePrice = item?.productCost || 0;
                    const effectivePrice = item?.taxPercentage ? basePrice * (1 + item.taxPercentage / 100) : basePrice;

                    // Use declaredLossQty from API if available, otherwise calculate
                    const calculatedLossQty = convertedFulfilled - convertedStoreFulfilled;
                    const lossQtyToUse = item?.declaredLossQty !== undefined && item?.declaredLossQty !== null
                        ? Number(getHigherUnitAndQuantity(item?.unit, item?.declaredLossQty)[0])
                        : calculatedLossQty;

                    return sum + (lossQtyToUse * effectivePrice);
                }, 0));
            }, 0);
            return (
                lossPrice < 0 ? (
                    <span className='text-nowrap text-[12px] text-green-600'>
                        Excess Receiving (₹{Math.abs(Number(lossPrice || 0)).toFixed(2)})
                    </span>
                ) : (
                    <span className='text-nowrap text-[12px]'>
                        ₹{Number(lossPrice || 0).toFixed(2)}
                    </span>
                )
            );
        },
        lossPercent: (obj) => {
            let totalDispatchedPrice = 0;
            let totalLossPrice = 0;

            obj?.poOrders?.forEach((po) => {
                po?.items?.forEach((item) => {
                    const [convertedFulfilled] = getHigherUnitAndQuantity(item?.unit, item?.fulfilled || 0);
                    const [convertedStoreFulfilled] = getHigherUnitAndQuantity(item?.unit, item?.storeFulfilled || 0);
                    const basePrice = item?.productCost || 0;
                    const effectivePrice = item?.taxPercentage ? basePrice * (1 + item.taxPercentage / 100) : basePrice;

                    totalDispatchedPrice += convertedFulfilled * effectivePrice;

                    // Use declaredLossQty from API if available, otherwise calculate
                    const calculatedLossQty = convertedFulfilled - convertedStoreFulfilled;
                    const lossQtyToUse = item?.declaredLossQty !== undefined && item?.declaredLossQty !== null
                        ? Number(getHigherUnitAndQuantity(item?.unit, item?.declaredLossQty)[0])
                        : calculatedLossQty;

                    totalLossPrice += lossQtyToUse * effectivePrice;
                });
            });

            const lossPercent = totalDispatchedPrice > 0 ? (totalLossPrice / totalDispatchedPrice) * 100 : 0;
            return (
                lossPercent < 0 ? (
                    <span className='text-nowrap text-[12px] text-green-600'>
                        Excess Receiving ({Math.abs(lossPercent).toFixed(2)}%)
                    </span>
                ) : (
                    <span className='text-nowrap text-[12px]'>
                        {lossPercent?.toFixed(2)}%
                    </span>
                )
            );
        },
        recoveredAtKitchen: (obj) => {
            const recoveredPrice = obj?.poOrders?.reduce?.((acc, po) => {
                return acc + (po?.items?.reduce((sum, item) => {
                    const recoveredQtyToUse = item?.declaredLossRecoveredQty !== undefined && item?.declaredLossRecoveredQty !== null
                        ? item?.declaredLossRecoveredQty
                        : (item?.recoveredAtKitchenQty || 0);
                    const [convertedQty] = getHigherUnitAndQuantity(item?.unit, recoveredQtyToUse);
                    const basePrice = item?.productCost || 0;
                    const effectivePrice = item?.taxPercentage ? basePrice * (1 + item.taxPercentage / 100) : basePrice;
                    const price = Number(convertedQty) * effectivePrice;
                    return sum + price;
                }, 0));
            }, 0);
            return (
                <span className='text-nowrap text-[12px]'>
                    ₹{Number(recoveredPrice || 0).toFixed(2)}
                </span>
            );
        },
        status: (obj) => {
            return <StatusBadge status={obj?.adjustmentBy} statusType={obj?.adjustmentBy} />;
        },
        action: (obj) => {
            const isViewOnly = obj?.status === 'ADJUSTED';
            return (
                <Tooltip title={isViewOnly ? 'View Details' : 'Edit'}>
                    <button
                        className="p-1 text-gray-600 hover:text-gray-800 transition-colors"
                        onClick={() => history.push({
                            pathname: `${urls.EXTENSION}${urls.TRANSIT_LOSS_DETAIL}`,
                            search: `roundId=${obj?.roundId}`,
                            state: {
                                prevPath: history.location.pathname + history.location.search,
                            },
                        })}
                    >
                        {isViewOnly ? (
                            <Eye size={18} />
                        ) : (
                            <ExternalLink size={18} />
                        )}
                    </button>
                </Tooltip>
            );
        },
    };

    const sortValueInfo = {
        transitDate: {
            type: 'string',
            value: 'transitDate',
        },
        dispatchedQty: {
            type: 'number',
            customValue: true,
            value: (obj) => {
                return obj?.poOrders?.reduce?.((acc, po) => {
                    return acc + (po?.items?.reduce((sum, item) => {
                        const [convertedQty] = getHigherUnitAndQuantity(item?.unit, item?.fulfilled || 0);
                        const basePrice = item?.productCost || 0;
                        const effectivePrice = item?.taxPercentage ? basePrice * (1 + item.taxPercentage / 100) : basePrice;
                        return sum + (Number(convertedQty) * effectivePrice);
                    }, 0));
                }, 0) || 0;
            },
        },
        receivedQty: {
            type: 'number',
            customValue: true,
            value: (obj) => {
                return obj?.poOrders?.reduce?.((acc, po) => {
                    return acc + (po?.items?.reduce((sum, item) => {
                        const [convertedQty] = getHigherUnitAndQuantity(item?.unit, item?.storeFulfilled || 0);
                        const basePrice = item?.productCost || 0;
                        const effectivePrice = item?.taxPercentage ? basePrice * (1 + item.taxPercentage / 100) : basePrice;
                        return sum + (Number(convertedQty) * effectivePrice);
                    }, 0));
                }, 0) || 0;
            },
        },
        lossQty: {
            type: 'number',
            customValue: true,
            value: (obj) => {
                const dispatchedPrice = obj?.poOrders?.reduce?.((acc, po) => {
                    return acc + (po?.items?.reduce((sum, item) => {
                        const [convertedQty] = getHigherUnitAndQuantity(item?.unit, item?.fulfilled || 0);
                        const basePrice = item?.productCost || 0;
                        const effectivePrice = item?.taxPercentage ? basePrice * (1 + item.taxPercentage / 100) : basePrice;
                        return sum + (Number(convertedQty) * effectivePrice);
                    }, 0));
                }, 0) || 0;
                const receivedPrice = obj?.poOrders?.reduce?.((acc, po) => {
                    return acc + (po?.items?.reduce((sum, item) => {
                        const [convertedQty] = getHigherUnitAndQuantity(item?.unit, item?.storeFulfilled || 0);
                        const basePrice = item?.productCost || 0;
                        const effectivePrice = item?.taxPercentage ? basePrice * (1 + item.taxPercentage / 100) : basePrice;
                        return sum + (Number(convertedQty) * effectivePrice);
                    }, 0));
                }, 0) || 0;
                return dispatchedPrice - receivedPrice;
            },
        },
        lossPercent: {
            type: 'number',
            customValue: true,
            value: (obj) => {
                const dispatchedPrice = obj?.poOrders?.reduce?.((acc, po) => {
                    return acc + (po?.items?.reduce((sum, item) => {
                        const [convertedQty] = getHigherUnitAndQuantity(item?.unit, item?.fulfilled || 0);
                        const basePrice = item?.productCost || 0;
                        const effectivePrice = item?.taxPercentage ? basePrice * (1 + item.taxPercentage / 100) : basePrice;
                        return sum + (Number(convertedQty) * effectivePrice);
                    }, 0));
                }, 0) || 0;
                const receivedPrice = obj?.poOrders?.reduce?.((acc, po) => {
                    return acc + (po?.items?.reduce((sum, item) => {
                        const [convertedQty] = getHigherUnitAndQuantity(item?.unit, item?.storeFulfilled || 0);
                        const basePrice = item?.productCost || 0;
                        const effectivePrice = item?.taxPercentage ? basePrice * (1 + item.taxPercentage / 100) : basePrice;
                        return sum + (Number(convertedQty) * effectivePrice);
                    }, 0));
                }, 0) || 0;
                const lossPrice = dispatchedPrice - receivedPrice;
                return dispatchedPrice > 0 ? (lossPrice / dispatchedPrice) * 100 : 0;
            },
        },
    };

    const handleExport = () => {
        const exportColumns = columns.filter((col) => !['poDetails', 'action'].includes(col.key));
        const headers = exportColumns.map((ele) => ele.title);

        const rows = transitLossList.map((obj) => {
            return exportColumns.map((col) => {
                const key = col.key;

                if (key === 'truckNumber') return obj?.vehicleNo || '';
                if (key === 'transitDate') {
                    const date = new Date(obj?.poOrders?.[0]?.PoStatusDateData?.[0]?.createdAt);
                    return moment(date).format('DD-MM-YYYY');
                }
                if (key === 'transitTime') {
                    const startime = moment(obj?.poOrders?.[0]?.PoStatusDateData?.[0]?.createdAt).format('hh:mm A');
                    const endTime = moment(obj?.poOrders?.[0]?.PoStatusDateData?.[0]?.endedAt).format('hh:mm A');
                    return `${startime} - ${endTime}`;
                }
                if (key === 'status') {
                    return obj?.adjustmentBy === 'AUTO' ? 'AUTO ACCEPTED' : obj?.adjustmentBy === 'MANUAL' ? 'MANUAL ADJUSTMENT' : 'PENDING ADJUSTMENT';
                }

                if (key === 'recoveredAtKitchen') {
                    const recoveredPrice = obj?.poOrders?.reduce?.((acc, po) => {
                        return acc + (po?.items?.reduce((sum, item) => {
                            const recoveredQtyToUse = item?.declaredLossRecoveredQty !== undefined && item?.declaredLossRecoveredQty !== null
                                ? item?.declaredLossRecoveredQty
                                : (item?.recoveredAtKitchenQty || 0);
                            const [convertedQty] = getHigherUnitAndQuantity(item?.unit, recoveredQtyToUse);
                            const basePrice = item?.productCost || 0;
                            const effectivePrice = item?.taxPercentage ? basePrice * (1 + item.taxPercentage / 100) : basePrice;
                            const price = Number(convertedQty) * effectivePrice;
                            return sum + price;
                        }, 0));
                    }, 0);
                    return Number(recoveredPrice || 0).toFixed(2);
                }

                const sortInfo = sortValueInfo[key];
                if (sortInfo?.customValue && typeof sortInfo.value === 'function') {
                    const val = sortInfo.value(obj);
                    return typeof val === 'number' ? Math.max(0, val.toFixed(2)) : Math.max(0, val);
                }

                return '';
            });
        });

        handleExcelDownload(headers, rows, `Transit_Loss_Report_${moment().format('YYYY-MM-DD_HH-mm')}`);
    };

    const handleFilterClick = () => {
        dispatch(drawerActions.toggleFiltersDrawer(true, {
            handleDataExport: () => handleExport()
        }));
    };
    const exportInfo = {
        showExportBtn: true,
    }

    return (
        <div className="flex flex-col h-full p-4">
            <TransitPoDetailsModal />
            <ImagesFinder filtersInfo={filtersInfo} onSubmit={fetchList} exportInfo={exportInfo} avoidInitialCall={!flag} />

            <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl font-bold text-gray-800">Transit Loss Logs</h1>
            </div>
            <div className='flex flex-1 items-baseline gap-3'>
                <div className="flex-1 min-w-0">
                    <GlobalSearch
                        searchDropDown={false}
                        setSelectedValue={setSearch}
                        placeholderText="Search by truck number, delivery location..."
                    />
                </div>
                <div className='flex-shrink-0'>
                    {filtersInfoChip(filtersInfo, filters)}
                </div>
                <Button
                    onClick={handleFilterClick}
                    className="flex items-center gap-2 px-4 py-2.5 text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap shadow-sm shrink-0"
                >
                    <span className="font-medium">Filter and export</span>
                    <Filter className="w-4 h-4" />
                </Button>
            </div>

            {/* Info Note */}
            <div className="flex items-center justify-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg">
                <Info className="text-red-600 flex-shrink-0" size={18} />
                <span className="text-sm text-red-700">
                    Note: Outlets must scan recoveries via QR code. Kitchens must record recoveries via console declaration.
                </span>
            </div>
            {/* Data Table */}
            <div className="h-[calc(100vh-280px)]">
                <VariantTable
                    data={filteredData()}
                    columns={columns}
                    customRowData={customRowData}
                    sortValueInfo={sortValueInfo}
                    isLoading={loading}
                    customInfoLine="To view transit loss logs, please choose a store from the filters."
                />
            </div>
        </div >
    );
}

export default TransitLoss;
